import { Grid } from '@mui/material';
import {
  HSpacer,
  RoutalDialog,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useState } from 'react';

import {
  CustomFieldDefinition,
  CustomFieldDefinitionModels,
  CustomFieldDefinitionTypes,
} from '@nx-smartmonkey/shared/domain';
import { FormArray } from '../../../components/form-fields/FormArray';
import { Project } from '../../../domain/project/Project';
import { intl } from '../../../helpers/IntGlobalProvider';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { ModelToIntlId } from './IntlMapper';
import { ProjectCustomFieldCategoryIcon, ProjectCustomFieldTypeIcon } from './ProjectCustomFieldIcons';

type ProjectCustomFieldCreateDialogProps = {
  open: boolean;
  project: Project;
  options: CustomFieldDefinitionModels[];
  selectedOption?: CustomFieldDefinitionModels;
  handleCreateCustomField: (customField: CustomFieldDefinition) => void;
  onCloseDialog: (event?: any) => void;
};

type CreateProjectCustomField = {
  custom_id: string;
  label: string;
  description: string;
  field_type: CustomFieldDefinitionTypes;
  field_model: CustomFieldDefinitionModels;
  enabled: boolean;
  options: string[];
  multiple: boolean;
};

const initialFieldState: CreateProjectCustomField = {
  custom_id: ``,
  label: ``,
  description: ``,
  field_type: `text`,
  field_model: `client`,
  enabled: true,
  options: [],
  multiple: false,
};

export const ProjectCustomFieldCreateDialog = ({
  open,
  project,
  options,
  selectedOption,
  handleCreateCustomField,
  onCloseDialog,
}: ProjectCustomFieldCreateDialogProps) => {
  const [field, setField] = useState<CustomFieldDefinition>(
    CustomFieldDefinition.create({
      project_id: project.id,
      organization_id: project.organization_id,
      ...initialFieldState,
      ...(selectedOption ? { field_model: selectedOption } : {}),
    })
  );

  const onClose = () => {
    setField(
      CustomFieldDefinition.create({
        project_id: project.id,
        organization_id: project.organization_id,
        ...initialFieldState,
      })
    );
    onCloseDialog();
  };

  return (
    <RoutalDialog
      id="project-create-custom-field-dialog"
      open={open}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }}
      onCloseDialog={onClose}
      titleContent={<Text variant="h6" intlId="projects.settings.fields.custom_fields.create_dialog.title" />}
      footerContent={
        <Grid container justifyContent="flex-end">
          <Button
            disabled={
              !field.custom_id ||
              !field.label ||
              (field.field_type === `categorical` && (field.options ?? []).length === 0)
            }
            onClick={() => {
              handleCreateCustomField(field);
              onClose();
            }}
            intlId={`projects.settings.fields.custom_fields.create_dialog.button`}
          />
        </Grid>
      }
    >
      <Grid item container direction="column" spacing={2} alignItems="center">
        <Grid item style={{ padding: `0 20px`, width: `100%` }}>
          <div
            style={{
              margin: `10px 0`,
              display: `flex`,
              alignItems: `start`,
              flexDirection: `column`,
            }}
          >
            <Text variant="label" intlId="projects.settings.fields.custom_fields.category" />

            <VSpacer small />

            <RoutalSelect
              id="select_field_model"
              style={{
                height: `36px`,
                margin: 0,
              }}
              popoverProps={{
                options: options.map(
                  (category, index): RoutalSelectMultiSelectOption => ({
                    key: category,
                    onClick: () => {
                      field.field_model = category;
                      setField(CustomFieldDefinition.create(field.props));
                    },
                    height: 55,
                    content: (
                      <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                        <Grid item>
                          <ProjectCustomFieldCategoryIcon
                            fieldModel={category}
                            selected={field.field_model === category}
                          />
                        </Grid>
                        <Grid item container direction="column" style={{ marginLeft: `8px` }}>
                          <Text
                            tagName="span"
                            style={{
                              textAlign: `justify`,
                              whiteSpace: `nowrap`,
                              overflow: `hidden`,
                              width: `100%`,
                              textOverflow: `ellipsis`,
                              cursor: `pointer`,
                              color:
                                field.field_model === category ? RoutalPalette.primary40 : RoutalPalette.secondary60,
                            }}
                            intlId={ModelToIntlId[category]}
                          />
                          <Text variant="label" intlId={`${ModelToIntlId[category]}.description`} />
                        </Grid>
                      </Grid>
                    ),
                  })
                ),
              }}
            >
              <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                <ProjectCustomFieldCategoryIcon fieldModel={field.field_model} />
                <HSpacer small />
                <Text
                  variant="body"
                  style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                  intlId={ModelToIntlId[field.field_model]}
                />
              </Grid>
            </RoutalSelect>

            <VSpacer medium />

            <Text variant="label" intlId="projects.settings.fields.custom_fields.label" />

            <VSpacer small />

            <InputText
              id="field_label"
              fieldValue={field.label}
              placeholderIntlId="projects.settings.fields.custom_fields.label_ph"
              validators={[
                {
                  errorMessage: <Text intlId="projects.settings.fields.custom_fields.label_required" />,
                  isInvalid: (text?: string): boolean => {
                    if (text && text !== ``) {
                      const valueText = text.trim();
                      return valueText.length < 1;
                    }
                    return true;
                  },
                },
              ]}
              onChange={(text: any) => {
                if (text) {
                  field.label = text;

                  const str = text.normalize(`NFD`).replace(/[\u0300-\u036f]/g, ``);

                  // In custom field creation. If ID is not set, create it with the same value of label.
                  const newId = str.toLowerCase().trim().replace(/ /g, `_`).replace(/\W/g, ``);

                  field.custom_id = newId;

                  setField(CustomFieldDefinition.create(field.props));
                } else {
                  field.label = ``;
                  setField(CustomFieldDefinition.create(field.props));
                }
              }}
              inputContainerStyles={{ width: `100%` }}
            />

            <VSpacer medium />

            <Text variant="label" intlId="projects.settings.fields.custom_fields.id" />

            <VSpacer small />

            <InputText
              id="field_id"
              fieldValue={field.custom_id}
              placeholderIntlId="projects.settings.fields.custom_fields.id_ph"
              validators={[
                {
                  errorMessage: <Text intlId="projects.settings.fields.custom_fields.id_required" />,
                  isInvalid: (text?: string): boolean => {
                    if (text && text !== ``) {
                      const valueText = text.trim();
                      return valueText.length < 1;
                    }
                    return true;
                  },
                },
                {
                  errorMessage: <Text intlId="projects.settings.fields.custom_fields.id_pattern_failed" />,
                  isInvalid: (text?: string): boolean => {
                    return !/^[a-z0-9_-]*$/.test(text ?? ``);
                  },
                },
              ]}
              onChange={(text: any) => {
                if (text) {
                  field.custom_id = text.toLowerCase();
                  setField(CustomFieldDefinition.create(field.props));
                } else {
                  field.custom_id = ``;
                  setField(CustomFieldDefinition.create(field.props));
                }
              }}
              inputContainerStyles={{ width: `100%` }}
            />

            <VSpacer medium />

            <Text variant="label" intlId="projects.settings.fields.custom_fields.description" />

            <VSpacer small />

            <InputText
              id="field_description"
              fieldValue={field.description}
              placeholderIntlId="projects.settings.fields.custom_fields.description_ph"
              onChange={(text: any) => {
                field.description = text ?? ``;
                setField(CustomFieldDefinition.create(field.props));

                return undefined;
              }}
              inputContainerStyles={{ width: `100%` }}
            />

            <VSpacer medium />

            <Text variant="label" intlId="projects.settings.fields.custom_fields.type" />

            <VSpacer small />

            <RoutalSelect
              id="select_field_type"
              style={{
                height: `36px`,
                margin: 0,
              }}
              popoverProps={{
                options: [
                  {
                    key: `text`,
                    content: (
                      <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                        <ProjectCustomFieldTypeIcon fieldType={`text`} />
                        <HSpacer small />
                        <Text
                          variant="body"
                          style={{ cursor: `pointer` }}
                          intlId="projects.settings.fields.custom_fields.types.text"
                        />
                      </Grid>
                    ),
                    onClick: () => {
                      field.field_type = `text`;
                      field.options = [];
                      field.multiple = false;
                      setField(CustomFieldDefinition.create(field.props));
                    },
                  },
                  {
                    key: `numerical`,
                    content: (
                      <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                        <ProjectCustomFieldTypeIcon fieldType={`numerical`} />
                        <HSpacer small />
                        <Text
                          variant="body"
                          style={{ cursor: `pointer` }}
                          intlId="projects.settings.fields.custom_fields.types.numerical"
                        />
                      </Grid>
                    ),
                    onClick: () => {
                      field.field_type = `numerical`;
                      field.options = [];
                      field.multiple = false;
                      setField(CustomFieldDefinition.create(field.props));
                    },
                  },
                  {
                    key: `boolean`,
                    content: (
                      <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                        <ProjectCustomFieldTypeIcon fieldType={`boolean`} />
                        <HSpacer small />
                        <Text
                          variant="body"
                          style={{ cursor: `pointer` }}
                          intlId="projects.settings.fields.custom_fields.types.boolean"
                        />
                      </Grid>
                    ),
                    onClick: () => {
                      field.field_type = `boolean`;
                      field.options = [];
                      field.multiple = false;
                      setField(CustomFieldDefinition.create(field.props));
                    },
                  },
                  {
                    key: `categorical`,
                    content: (
                      <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                        <ProjectCustomFieldTypeIcon fieldType={`categorical`} />
                        <HSpacer small />
                        <Text
                          variant="body"
                          style={{ cursor: `pointer` }}
                          intlId="projects.settings.fields.custom_fields.types.categorical"
                        />
                      </Grid>
                    ),
                    onClick: () => {
                      field.field_type = `categorical`;
                      field.multiple = false;
                      setField(CustomFieldDefinition.create(field.props));
                    },
                  },
                  {
                    key: `categorical_multiple`,
                    content: (
                      <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                        <ProjectCustomFieldTypeIcon fieldType={`categorical_multiple`} />
                        <HSpacer small />
                        <Text
                          variant="body"
                          style={{ cursor: `pointer` }}
                          intlId="projects.settings.fields.custom_fields.types.categorical_multiple"
                        />
                      </Grid>
                    ),
                    onClick: () => {
                      field.field_type = `categorical`;
                      field.multiple = true;
                      setField(CustomFieldDefinition.create(field.props));
                    },
                  },
                ],
              }}
            >
              <Grid container direction="row" wrap="nowrap" style={{ width: `100%` }} alignItems="center">
                <ProjectCustomFieldTypeIcon fieldType={field.field_type} />
                <HSpacer small />
                <Text
                  variant="body"
                  style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                  intlId={`projects.settings.fields.custom_fields.types.${field.field_type}`}
                />
              </Grid>
            </RoutalSelect>

            {field.field_type === `categorical` ? (
              <>
                <VSpacer medium />
                <FormArray
                  key={`categorical-options`}
                  array={field.options}
                  inputPlaceholder={intl.formatMessage({
                    id: `projects.settings.fields.custom_fields.categorical_multiple.new_option`,
                  })}
                  onUpdate={(value: any) => {
                    field.options = value;
                    setField(CustomFieldDefinition.create(field.props));
                  }}
                />
              </>
            ) : null}
          </div>
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
